import { useEffect, useRef, useState } from 'react';
import type { BulkReferralResponseDto, BulkReferralRowResult } from '../../../types/bulk-referral.type';
import { InspectionPanel } from './reconciliation/InspectionPanel';
import { KpiStrip } from './reconciliation/KpiStrip';
import { ProblemsBlankSlate } from './reconciliation/ProblemsBlankSlate';
import { ReconciliationFilterBar } from './reconciliation/ReconciliationFilterBar';
import { ReconciliationFooter } from './reconciliation/ReconciliationFooter';
import { ReconciliationTable } from './reconciliation/ReconciliationTable';
import { defaultFilterFor, filterRows, type ReconciliationFilter } from './reconciliation/reconciliationHelpers';

type ReconciliationStageProps = {
  data: BulkReferralResponseDto;
  onImport: () => void;
  isImporting?: boolean;
  commitError?: string | null;
  /** Selection is also exposed to the caller, in case something outside this stage needs it. */
  onRowSelect?: (row: BulkReferralRowResult | null) => void;
};

const FLASH_DURATION_MS = 1400;

export const ReconciliationStage = ({
  data,
  onImport,
  isImporting = false,
  commitError,
  onRowSelect,
}: ReconciliationStageProps) => {
  const [filter, setFilter] = useState<ReconciliationFilter>(() => defaultFilterFor(data.rows));
  const [selectedRowNumber, setSelectedRowNumber] = useState<number | null>(null);
  const [flashingRowNumber, setFlashingRowNumber] = useState<number | null>(null);
  const flashTimeout = useRef<number | undefined>(undefined);

  useEffect(() => {
    setFilter(defaultFilterFor(data.rows));
    setSelectedRowNumber(null);
  }, [data]);

  useEffect(() => () => window.clearTimeout(flashTimeout.current), []);

  const errorRows = data.rows.filter((row) => row.status === 'ERROR');
  const visibleRows = filterRows(data.rows, filter);
  const selectedRow = data.rows.find((row) => row.rowNumber === selectedRowNumber) ?? null;

  const selectRow = (row: BulkReferralRowResult | null) => {
    setSelectedRowNumber(row?.rowNumber ?? null);
    onRowSelect?.(row);
  };

  const jumpToFirstProblem = () => {
    const first = errorRows[0];
    if (!first) return;
    setFilter('all');
    selectRow(first);
    setFlashingRowNumber(first.rowNumber);
    window.setTimeout(() => {
      document.getElementById(`reconciliation-row-${first.rowNumber}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
    window.clearTimeout(flashTimeout.current);
    flashTimeout.current = window.setTimeout(() => setFlashingRowNumber(null), FLASH_DURATION_MS);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <KpiStrip total={data.summary.totalRows} reconciled={data.summary.resolvedRows} withProblems={data.summary.errorRows} />

      <ReconciliationFilterBar
        filter={filter}
        problemsCount={errorRows.length}
        allCount={data.rows.length}
        onFilterChange={setFilter}
        showJumpToFirstProblem={errorRows.length > 0}
        onJumpToFirstProblem={jumpToFirstProblem}
      />

      <div className="relative flex min-h-[18rem] flex-1">
        {visibleRows.length === 0 ? (
          <ProblemsBlankSlate totalRows={data.rows.length} onShowAll={() => setFilter('all')} />
        ) : (
          <ReconciliationTable
            rows={visibleRows}
            selectedRowNumber={selectedRowNumber}
            flashingRowNumber={flashingRowNumber}
            onSelectRow={selectRow}
          />
        )}
        <InspectionPanel row={selectedRow} onClose={() => selectRow(null)} />
      </div>

      <ReconciliationFooter
        totalRows={data.summary.totalRows}
        resolvedRows={data.summary.resolvedRows}
        errorRows={data.summary.errorRows}
        newContacts={data.summary.newContacts}
        reusedContacts={data.summary.reusedContacts}
        invitesToSend={data.summary.invitesToSend}
        onImport={onImport}
        isImporting={isImporting}
        commitError={commitError}
      />
    </div>
  );
};
